import { useState } from 'react';
import { clsx } from 'clsx';
import type { Evaluation, PredefinedRedFlag } from '../../types';
import { StatusBadge } from '../common/StatusBadge';
import { Card } from '../common/Card';
import { RedFlagCard } from './RedFlagCard';
import { RagnarokSources } from './RagnarokSources';
import { normalizeRedFlags } from '../../lib/redFlags';

// Minimal framework shape needed for display (works with both PublicFramework and FrameworkConfig)
interface FrameworkForDisplay {
  sections: {
    id: string;
    name: string;
    weight: number;
    criteria: {
      id: string;
      name: string;
    }[];
  }[];
}

interface AIEvaluationProps {
  evaluation: Evaluation;
  framework?: FrameworkForDisplay;
  redFlagDefinitions?: PredefinedRedFlag[];
}

export function AIEvaluation({ evaluation, framework, redFlagDefinitions = [] }: AIEvaluationProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const scoreColor = getScoreColor(evaluation.total_score);

  // Normalize red flags for display
  const normalizedFlags = normalizeRedFlags(evaluation.red_flags, redFlagDefinitions);

  return (
    <Card className="overflow-hidden">
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-action font-mono text-sm">AI</span>
            <span className="text-wolf-300 font-medium">Evaluation</span>
          </div>
          <StatusBadge status={evaluation.decision} />
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <span className={clsx('font-mono text-2xl font-bold', scoreColor)}>
              {evaluation.total_score.toFixed(1)}
            </span>
            <span className="text-wolf-500 text-sm ml-1">/100</span>
          </div>
          <svg
            className={clsx(
              'w-5 h-5 text-wolf-400 transition-transform',
              isExpanded && 'rotate-180'
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="mt-6 space-y-6 border-t border-wolf-700/50 pt-6">
          {/* Red flags */}
          {normalizedFlags.length > 0 && (
            <div>
              <h4 className="text-red-400 font-medium mb-3 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                Red Flags Detected ({normalizedFlags.length})
              </h4>
              <div className="space-y-2">
                {normalizedFlags.map((flag) => (
                  <RedFlagCard key={flag.id} flag={flag} />
                ))}
              </div>
            </div>
          )}

          {/* Summary narrative */}
          {evaluation.summary?.narrative && (
            <div>
              <h4 className="text-wolf-300 font-medium mb-2">Summary</h4>
              <p className="text-wolf-400 text-sm whitespace-pre-wrap">
                {evaluation.summary.narrative}
              </p>
            </div>
          )}

          {/* Strengths and gaps */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {evaluation.summary?.strengths && (
              <div className="bg-action/10 border border-action/30 p-4">
                <h4 className="text-action font-medium mb-2">Strengths</h4>
                <p className="text-wolf-300 text-sm">{evaluation.summary.strengths}</p>
              </div>
            )}
            {evaluation.summary?.gaps && (
              <div className="bg-amber-500/10 border border-amber-500/30 p-4">
                <h4 className="text-amber-400 font-medium mb-2">Gaps</h4>
                <p className="text-wolf-300 text-sm">{evaluation.summary.gaps}</p>
              </div>
            )}
          </div>

          {/* Recommendation */}
          {evaluation.summary?.recommendation && (
            <div className="bg-wolf-800/50 p-4">
              <h4 className="text-wolf-300 font-medium mb-2">Recommendation</h4>
              <p className="text-wolf-400 text-sm">{evaluation.summary.recommendation}</p>
            </div>
          )}

          {/* Per-criterion scores */}
          <div>
            <h4 className="text-wolf-300 font-medium mb-4">Detailed Scores</h4>
            <div className="space-y-4">
              {framework?.sections.map((section) => (
                <div key={section.id} className="border border-wolf-700/50 p-4">
                  <h5 className="text-wolf-100 font-medium mb-3">
                    {section.id}. {section.name}
                    <span className="text-wolf-500 font-normal ml-2">
                      ({(section.weight * 100).toFixed(0)}% weight)
                    </span>
                  </h5>
                  <div className="space-y-3">
                    {section.criteria.map((criterion) => {
                      const score = evaluation.scores[criterion.id];
                      if (!score) return null;
                      return (
                        <div key={criterion.id} className="flex gap-4">
                          <div className="flex-shrink-0 w-12">
                            <span
                              className={clsx(
                                'font-mono text-lg font-bold',
                                getIndividualScoreColor(score.score)
                              )}
                            >
                              {score.score}
                            </span>
                            <span className="text-wolf-500 text-xs">/5</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-wolf-100 text-sm">{criterion.name}</div>
                            <div className="text-wolf-400 text-xs mt-1">{score.rationale}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              {/* Show scores without framework context */}
              {!framework && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(evaluation.scores).map(([id, score]) => (
                    <div key={id} className="flex gap-3 bg-wolf-800/30 p-3">
                      <div className="flex-shrink-0">
                        <span
                          className={clsx(
                            'font-mono text-lg font-bold',
                            getIndividualScoreColor(score.score)
                          )}
                        >
                          {score.score}
                        </span>
                        <span className="text-wolf-500 text-xs">/5</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-wolf-100 text-sm font-mono">{id}</div>
                        <div className="text-wolf-400 text-xs mt-1 truncate">{score.rationale}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Ragnarok company knowledge sources */}
          <RagnarokSources ragnarokContext={evaluation.ragnarok_context} />
        </div>
      )}
    </Card>
  );
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-action';
  if (score >= 60) return 'text-amber-400';
  if (score >= 40) return 'text-orange-400';
  return 'text-red-400';
}

function getIndividualScoreColor(score: number): string {
  if (score >= 4) return 'text-action';
  if (score >= 3) return 'text-amber-400';
  if (score >= 2) return 'text-orange-400';
  return 'text-red-400';
}
